import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import { styled } from '@mui/material/styles';
import { useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  bindCardSelectionDisabled,
  CardsSelectionProvider,
  InternalContextProvider,
  type ISwimlaneProps,
  SwimlaneContextProvider,
  SwimlaneStateProvider,
  useSwimlaneRootProps,
} from './context';
import { SwimlaneAxisLabelLeft, SwimlaneAxisLabelTop } from './layout/axis/SwimlaneAxisLabel';
import { CardContextMenu } from './layout/body/cards/cardContextMenu';
import { TopLeftCorner } from './layout/body/TopLeftCorner';
import { GRID_GAP, GROUP_COL_INNER_GAP, ROW_HEADER_WIDTH } from './layout/constants';
import { ExportLayoutRoot } from './layout/ExportLayoutRoot';
import { AddHeader } from './layout/headers/AddHeader';
import { useCardEventHandlers } from './layout/hooks/useCardEventHandlers';
import { VirtualizerRoot } from './layout/VirtualizerRoot';

export type { ISwimlaneProps };

/**
 * Builds the CSS Grid template areas string based on configuration flags.
 * The grid structure is conditionally built based on:
 * - hasAxisLabelColumn: Whether a column axis label is present
 * - hasAxisLabelRow: Whether a row axis label is present
 * - hasRowHeaders: Whether row headers are present
 * - isAddColumnHeaderButtonVisible: Whether the add column button should be visible
 * - isAddRowHeaderButtonVisible: Whether the add row button should be visible
 */
function buildGridTemplateAreas(config: {
  hasAxisLabelColumn: boolean;
  hasAxisLabelRow: boolean;
  hasRowHeaders: boolean;
  isAddColumnHeaderButtonVisible: boolean;
  isAddRowHeaderButtonVisible: boolean;
}): string {
  const {
    hasAxisLabelColumn,
    hasAxisLabelRow,
    hasRowHeaders,
    isAddColumnHeaderButtonVisible,
    isAddRowHeaderButtonVisible,
  } = config;

  let gridTemplateAreas = '';

  // Row 1: Axis label row (if present)
  if (hasAxisLabelColumn) {
    if (hasAxisLabelRow) {
      gridTemplateAreas += '"erase erase axisX axisX"\n';
    } else {
      gridTemplateAreas += '"erase axisX axisX"\n';
    }
  }

  // Row 2: Column headers row
  if (isAddColumnHeaderButtonVisible) {
    if (hasAxisLabelRow) {
      gridTemplateAreas += '"erase erase colH addCol"\n';
    } else {
      gridTemplateAreas += '"erase colH addCol"\n';
    }
  } else {
    // When the add-column header button is hidden, colH spans both columns
    if (hasAxisLabelRow) {
      gridTemplateAreas += '"erase erase colH colH"\n';
    } else {
      gridTemplateAreas += '"erase colH colH"\n';
    }
  }

  // Row 3: Main grid content and row headers
  const rowHeaderCell = hasRowHeaders ? 'rowH' : '.';
  if (hasAxisLabelRow) {
    gridTemplateAreas += `"axisY ${rowHeaderCell} grid grid"\n`;
  } else {
    gridTemplateAreas += `"${rowHeaderCell} grid grid"\n`;
  }

  // Row 4: Add row button row
  // Only allocate addRow cell if both hasRowHeaders AND isAddRowHeaderButtonVisible are true
  const addRowCell = hasRowHeaders && isAddRowHeaderButtonVisible ? 'addRow' : '.';
  if (hasAxisLabelRow) {
    gridTemplateAreas += `"axisY ${addRowCell} . ."`;
  } else {
    gridTemplateAreas += `"${addRowCell} . ."`;
  }

  return gridTemplateAreas;
}

export const Swimlane = (props: ISwimlaneProps) => (
  <SwimlaneContextProvider {...props}>
    <SwimlaneBase data-dd-action-name="swimlane - general" />
  </SwimlaneContextProvider>
);

const SwimlaneBase = () => {
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const {
    rowHeadersWidth,
    isAddColumnHeaderButtonVisible,
    isAddRowHeaderButtonVisible,
    readOnly,
    maxSelectedCards,
    rowHeaders,
    columnAxisLabelText,
    rowAxisLabelText,
    rowHeaderOrientation,
    backgroundColor,
    cells,
    items,
    isPngPublicationTemplate,
  } = useSwimlaneRootProps();

  const hasGroupHeaders = rowHeaders.some((h) => 'isGroupHeader' in h && h.isGroupHeader === true);
  const subRowHeaderWidth =
    rowHeaderOrientation === 'vertical' ? ROW_HEADER_WIDTH.vertical.value : rowHeadersWidth;
  const rowHeaderContainerWidth = hasGroupHeaders
    ? ROW_HEADER_WIDTH.vertical.value + GROUP_COL_INNER_GAP.value + subRowHeaderWidth
    : subRowHeaderWidth;
  const hasRowHeaders = rowHeaders.length > 0;
  const hasAxisLabelColumn = typeof columnAxisLabelText === 'string';
  const hasAxisLabelRow = typeof rowAxisLabelText === 'string';

  const cardEventDelegationProps = useCardEventHandlers();

  const colAxisLabelRef = useRef<HTMLDivElement>(null);
  const rowAxisLabelRef = useRef<HTMLDivElement>(null);

  return (
    <AutoSizer>
      {({ height, width }: { height: number; width: number }) => {
        // Compute grid template from configuration flags (axis labels, headers, add buttons)
        const gridTemplateRows = hasAxisLabelColumn ? '32px 32px auto 1fr' : '32px auto 1fr';

        const axisLabelColumnWidth = hasAxisLabelRow ? '32px ' : '';
        const rowHeaderColumnWidth = hasRowHeaders ? `${rowHeaderContainerWidth}px` : '0px';
        const gridTemplateColumns = `${axisLabelColumnWidth}${rowHeaderColumnWidth} auto 1fr`;

        // Build grid template areas from configuration
        const gridTemplateAreas = buildGridTemplateAreas({
          hasAxisLabelColumn,
          hasAxisLabelRow,
          hasRowHeaders,
          isAddColumnHeaderButtonVisible,
          isAddRowHeaderButtonVisible,
        });

        return (
          // The Swimlane base grid is conditionally sized based on axis labels and hidden headers
          // Modifications to the grid must be done with caution!
          // Verify the behavior of: Top left corner, axis labels, and add headers. Each during resizing and scrolling.
          // Also verify the behavior when resizing horizontal row headers
          <StyledScopedCssBaseline
            ref={(node) => setScrollContainer(node)}
            backgroundColor={backgroundColor}
            style={{
              height,
              width,
              backgroundColor,
              gridGap: GRID_GAP.value,
              gridTemplateColumns,
              gridTemplateRows,
              gridTemplateAreas,
            }}
            {...cardEventDelegationProps}
            {...bindCardSelectionDisabled()}
            data-publicationid="swimlane-main-scrollable-container"
          >
            <SwimlaneStateProvider cells={cells} items={items}>
              <TopLeftCorner />
              {hasAxisLabelColumn && <SwimlaneAxisLabelTop ref={colAxisLabelRef} />}
              {hasAxisLabelRow && <SwimlaneAxisLabelLeft ref={rowAxisLabelRef} />}
              {isAddColumnHeaderButtonVisible && <AddHeader level={1} orientation="column" />}
              {hasRowHeaders && isAddRowHeaderButtonVisible && (
                <AddHeader level={2} orientation="row" />
              )}
              <InternalContextProvider data={{ colAxisLabelRef, rowAxisLabelRef }}>
                {scrollContainer && (
                  <CardsSelectionProvider
                    container={scrollContainer}
                    readOnly={readOnly}
                    maxSelectedCards={maxSelectedCards}
                  >
                    {isPngPublicationTemplate ? (
                      <ExportLayoutRoot scrollContainer={scrollContainer} />
                    ) : (
                      <VirtualizerRoot scrollContainer={scrollContainer} />
                    )}
                  </CardsSelectionProvider>
                )}
              </InternalContextProvider>
              <CardContextMenu />
            </SwimlaneStateProvider>
          </StyledScopedCssBaseline>
        );
      }}
    </AutoSizer>
  );
};

const StyledScopedCssBaseline = styled(ScopedCssBaseline, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{ backgroundColor?: string }>(({ theme, backgroundColor }) => ({
  display: 'grid',
  overflow: 'auto',
  fontFamily: theme.typography.fontFamily,
  // Override MUI ScopedCssBaseline's default white background
  backgroundColor: backgroundColor || theme.palette.background.default,
}));
